import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useProjectStore, usePoleStore, useCableStore } from '../../stores';
import { Pole, Cable } from '../../types';
import './TopologyView.css';
import { poleTypeLabel, poleTypeIcon } from '../../utils/poleLabels';

// Helper function to lighten colors
const lightenColor = (color: string, percent: number): string => {
  const num = parseInt(color.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
};

interface TopologyNode {
  id: string;
  type: 'olt' | 'pole' | 'customer' | 'passthrough' | 'splice';
  pole?: Pole;
  x: number;
  y: number;
  level: number;
  label?: string; // For virtual nodes (passthrough/splice)
}

interface TopologyLink {
  from: string;
  to: string;
  cable?: Cable;
  fiberNumber?: number;
  color?: string;
  connectionType?: 'direct' | 'passthrough' | 'splice' | 'splitter';
  intermediatePoles?: string[]; // Names of poles where passthrough/splice happens
}

/** Splitter aj s tým, či pochádza zo zákazníckej kazety (virtuálneho dvojčaťa). */
interface SplitterZKazety {
  splitter: any;
  zoZakaznickej: boolean;
}

export const TopologyView: React.FC = () => {
  const { currentProject } = useProjectStore();
  const { poles, fetchPoles } = usePoleStore();
  const { cables, fetchCables } = useCableStore();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedPole, setSelectedPole] = useState<Pole | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 1200, height: 800 });
  const [nodes, setNodes] = useState<TopologyNode[]>([]);
  const [links, setLinks] = useState<TopologyLink[]>([]);

  // Zoom and Pan state
  const [scale, setScale] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });

  // Layout direction state
  const [layoutDirection, setLayoutDirection] = useState<'TB' | 'LR'>('TB');

  // Update canvas size when container resizes
  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCanvasSize({
          width: Math.max(1200, rect.width - 40),
          height: Math.max(800, rect.height - 40),
        });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  // Fetch data when project changes
  useEffect(() => {
    if (currentProject) {
      fetchPoles(currentProject.id);
      fetchCables(currentProject.id);
    }
  }, [currentProject, fetchPoles, fetchCables]);

  // Build topology graph
  useEffect(() => {
    if (!poles.length || !cables.length) return;

    const newNodes: TopologyNode[] = [];
    const newLinks: TopologyLink[] = [];

    // Find OLT or root pole (pole with OLT ports or splitter that has no incoming cables)
    let olt = poles.find((p: Pole) => p.pole_type === 'olt');

    // If no OLT found, find pole with OLT port configuration
    if (!olt) {
      olt = poles.find((p: Pole) =>
        p.cassette?.olt_port_configs && p.cassette.olt_port_configs.length > 0
      );
    }

    // If still no OLT, find root poles (poles with no incoming cables)
    if (!olt) {
      const rootPoles = poles.filter((p: Pole) => {
        const hasIncoming = cables.some((c: Cable) => c.to_pole_id === p.id);
        return !hasIncoming;
      });
      if (rootPoles.length > 0) {
        olt = rootPoles[0];
      }
    }

    if (!olt) {
      console.warn('⚠️ No OLT or root pole found');
      return;
    }

    console.log(`🎯 Starting topology from: ${olt.name} (ID: ${olt.id}, Type: ${olt.pole_type})`);

    // Build tree structure using BFS
    const visited = new Set<number>();
    const queue: Array<{ pole: Pole; level: number }> = [{ pole: olt, level: 0 }];
    const levelNodes: Map<number, Pole[]> = new Map();

    while (queue.length > 0) {
      const { pole, level } = queue.shift()!;
      if (visited.has(pole.id)) continue;
      visited.add(pole.id);

      // Skip virtual poles (customer cassettes) in topology view
      if (pole.is_virtual) {
        console.log(`⏭️  Skipping virtual pole: ${pole.name} (ID: ${pole.id})`);
        continue;
      }

      console.log(`🔍 Visiting pole: ${pole.name} (ID: ${pole.id}) at level ${level}`);

      if (!levelNodes.has(level)) {
        levelNodes.set(level, []);
      }
      levelNodes.get(level)!.push(pole);

      // Find connected poles (both directions - from and to)
      // Skip virtual cables (connecting to customer cassettes)
      const outgoingCables = cables.filter((c: Cable) =>
        (c.from_pole_id === pole.id || c.to_pole_id === pole.id) && !c.is_virtual
      );

      console.log(`  📡 Found ${outgoingCables.length} connected cables`);

      for (const cable of outgoingCables) {
        // Determine next pole (could be from or to depending on direction)
        const nextPoleId = cable.from_pole_id === pole.id
          ? cable.to_pole_id
          : cable.from_pole_id;
        const nextPole = poles.find((p: Pole) => p.id === nextPoleId);

        console.log(`    🔗 Cable #${cable.id}: ${pole.name} ↔ ${nextPole?.name || 'Unknown'}`);

        // Skip virtual poles
        if (nextPole && !nextPole.is_virtual) {
          if (!visited.has(nextPole.id)) {
            console.log(`      ✅ Adding ${nextPole.name} to queue`);
            queue.push({ pole: nextPole, level: level + 1 });
          } else {
            console.log(`      ⏭️  ${nextPole.name} already visited`);
          }

          // Always add link, even if pole was already visited (for multiple connections)
          newLinks.push({
            from: `pole-${pole.id}`,
            to: `pole-${nextPole.id}`,
            cable,
            connectionType: 'direct',
          });
        }
      }

      // Also check for pass-through connections in cassette
      if (pole.cassette?.pass_through_cables) {
        console.log(`  🔄 Found ${pole.cassette.pass_through_cables.length} pass-through cables`);
        for (const passThrough of pole.cassette.pass_through_cables) {
          // Find the cables involved in pass-through (skip virtual cables)
          const fromCable = cables.find((c: Cable) => c.id === passThrough.from_cable_id && !c.is_virtual);
          const toCable = cables.find((c: Cable) => c.id === passThrough.to_cable_id && !c.is_virtual);

          console.log(`    🔄 Passthrough: Cable #${passThrough.from_cable_id} → Cable #${passThrough.to_cable_id}`);

          if (fromCable && toCable) {
            // Find the pole on the other end of the TO cable
            const nextPoleId = toCable.from_pole_id === pole.id
              ? toCable.to_pole_id
              : toCable.from_pole_id;
            const nextPole = poles.find((p: Pole) => p.id === nextPoleId);

            console.log(`      → Next pole via passthrough: ${nextPole?.name || 'Unknown'}`);

            // Skip virtual poles
            if (nextPole && !nextPole.is_virtual) {
              if (!visited.has(nextPole.id)) {
                console.log(`      ✅ Adding ${nextPole.name} to queue via passthrough`);
                queue.push({ pole: nextPole, level: level + 1 });
              } else {
                console.log(`      ⏭️  ${nextPole.name} already visited (passthrough)`);
              }

              // Always add link, even if pole was already visited
              // Mark this link as going through a passthrough node
              newLinks.push({
                from: `pole-${pole.id}`,
                to: `pole-${nextPole.id}`,
                cable: toCable,
                connectionType: 'passthrough',
                intermediatePoles: [pole.name],
              });
            }
          }
        }
      }

      // Also check for splice connections in cassette
      if (pole.cassette?.splices) {
        console.log(`  🔗 Found ${pole.cassette.splices.length} splices`);
        for (const splice of pole.cassette.splices) {
          // Skip OLT port splices (they don't lead to other poles)
          if (splice.from_source_type === 'olt_port') {
            console.log(`    🔗 Splice from OLT port ${splice.from_olt_port} → Cable #${splice.to_cable_id} (skipping - OLT internal)`);
            continue;
          }

          // Find the cables involved in splice
          const fromCableId = splice.from_cable_id;
          const toCableId = splice.to_cable_id;

          // If splice connects two different cables, follow the connection (skip virtual cables)
          if (fromCableId && toCableId && fromCableId !== toCableId) {
            const toCable = cables.find((c: Cable) => c.id === toCableId && !c.is_virtual);

            console.log(`    🔗 Splice: Cable #${fromCableId} fiber #${splice.from_fiber} → Cable #${toCableId} fiber #${splice.to_fiber}`);

            if (toCable) {
              // Find the pole on the other end of the TO cable
              const nextPoleId = toCable.from_pole_id === pole.id
                ? toCable.to_pole_id
                : toCable.from_pole_id;
              const nextPole = poles.find((p: Pole) => p.id === nextPoleId);

              console.log(`      → Next pole via splice: ${nextPole?.name || 'Unknown'}`);

              // Skip virtual poles
              if (nextPole && !nextPole.is_virtual) {
                if (!visited.has(nextPole.id)) {
                  console.log(`      ✅ Adding ${nextPole.name} to queue via splice`);
                  queue.push({ pole: nextPole, level: level + 1 });
                } else {
                  console.log(`      ⏭️  ${nextPole.name} already visited (splice)`);
                }

                // Always add link, even if pole was already visited
                newLinks.push({
                  from: `pole-${pole.id}`,
                  to: `pole-${nextPole.id}`,
                  cable: toCable,
                  connectionType: 'splice',
                  intermediatePoles: [pole.name],
                });
              }
            }
          } else {
            console.log(`    🔗 Splice within same cable #${fromCableId} (fiber #${splice.from_fiber} → #${splice.to_fiber}) - no pole traversal`);
          }
        }
      }

      // Also check for splitter connections in cassette
      if (pole.cassette?.splitters) {
        console.log(`  🔀 Found ${pole.cassette.splitters.length} splitters`);
        for (const splitter of pole.cassette.splitters) {
          // Check each output of the splitter (skip virtual cables)
          for (const output of splitter.outputs) {
            if (output.cable_id) {
              const outputCable = cables.find((c: Cable) => c.id === output.cable_id && !c.is_virtual);

              console.log(`    🔀 Splitter ${splitter.type} output → Cable #${output.cable_id} fiber #${output.fiber}`);

              if (outputCable) {
                // Find the pole on the other end of the output cable
                const nextPoleId = outputCable.from_pole_id === pole.id
                  ? outputCable.to_pole_id
                  : outputCable.from_pole_id;
                const nextPole = poles.find((p: Pole) => p.id === nextPoleId);

                console.log(`      → Next pole via splitter: ${nextPole?.name || 'Unknown'}`);

                // Skip virtual poles
                if (nextPole && !nextPole.is_virtual) {
                  if (!visited.has(nextPole.id)) {
                    console.log(`      ✅ Adding ${nextPole.name} to queue via splitter`);
                    queue.push({ pole: nextPole, level: level + 1 });
                  } else {
                    console.log(`      ⏭️  ${nextPole.name} already visited (splitter)`);
                  }

                  // Always add link, even if pole was already visited
                  newLinks.push({
                    from: `pole-${pole.id}`,
                    to: `pole-${nextPole.id}`,
                    cable: outputCable,
                    connectionType: 'splitter',
                  });
                }
              }
            }
          }
        }
      }
    }

    // Create virtual nodes for passthrough and splice connections
    const virtualNodes: Array<{ id: string; type: 'passthrough' | 'splice'; label: string; fromPoleId: number; toPoleId: number; linkIndex: number }> = [];
    const linksToReplace: number[] = [];

    console.log('🔍 Checking links for virtual nodes:', newLinks.length);

    newLinks.forEach((link, index) => {
      if (link.connectionType === 'passthrough' && link.intermediatePoles && link.intermediatePoles.length > 0) {
        console.log(`  ✅ Found passthrough link: ${link.from} -> ${link.to}, intermediate: ${link.intermediatePoles.join(', ')}`);
        const fromPoleId = parseInt(link.from.replace('pole-', ''));
        const toPoleId = parseInt(link.to.replace('pole-', ''));
        const virtualNodeId = `passthrough-${fromPoleId}-${toPoleId}`;

        virtualNodes.push({
          id: virtualNodeId,
          type: 'passthrough',
          label: `${link.intermediatePoles[0]} ⇢`,
          fromPoleId,
          toPoleId,
          linkIndex: index,
        });

        linksToReplace.push(index);
      } else if (link.connectionType === 'splice' && link.intermediatePoles && link.intermediatePoles.length > 0) {
        console.log(`  ✅ Found splice link: ${link.from} -> ${link.to}, intermediate: ${link.intermediatePoles.join(', ')}`);
        const fromPoleId = parseInt(link.from.replace('pole-', ''));
        const toPoleId = parseInt(link.to.replace('pole-', ''));
        const virtualNodeId = `splice-${fromPoleId}-${toPoleId}`;

        virtualNodes.push({
          id: virtualNodeId,
          type: 'splice',
          label: `${link.intermediatePoles[0]} 🔗`,
          fromPoleId,
          toPoleId,
          linkIndex: index,
        });

        linksToReplace.push(index);
      }
    });

    console.log(`🎯 Created ${virtualNodes.length} virtual nodes:`, virtualNodes);

    // Replace links with virtual nodes - build final links array
    const finalLinks: TopologyLink[] = [];
    newLinks.forEach((link, index) => {
      if (linksToReplace.includes(index)) {
        // Find the virtual node for this link
        const virtualNode = virtualNodes.find(vn => vn.linkIndex === index);
        if (virtualNode) {
          // Split into two links: from -> virtual -> to
          finalLinks.push({
            from: link.from,
            to: virtualNode.id,
            cable: link.cable,
            connectionType: 'direct',
          });
          finalLinks.push({
            from: virtualNode.id,
            to: link.to,
            cable: link.cable,
            connectionType: 'direct',
          });
        }
      } else {
        finalLinks.push(link);
      }
    });

    // Position nodes with improved layout
    const canvasWidth = canvasSize.width;
    const canvasHeight = canvasSize.height;
    const minNodeSpacing = 120; // Minimum spacing between nodes
    const levelSpacing = 180; // Spacing between levels

    // Calculate optimal positioning for each level
    levelNodes.forEach((polesInLevel, level) => {
      const nodesInLevel = polesInLevel.length;

      // Sort poles by type for better visual organization
      const sortedPoles = [...polesInLevel].sort((a, b) => {
        const typeOrder: Record<string, number> = {
          'olt': 0,
          'splitter_point': 1,
          'pole': 2,
          'building': 3,
          'manhole': 4
        };
        return (typeOrder[a.pole_type] || 5) - (typeOrder[b.pole_type] || 5);
      });

      if (layoutDirection === 'TB') {
        // Top-Bottom layout (original)
        const levelY = 100 + (level * levelSpacing);
        const totalWidth = Math.max(canvasWidth - 200, nodesInLevel * minNodeSpacing);
        const nodeSpacing = totalWidth / (nodesInLevel + 1);

        sortedPoles.forEach((pole, index) => {
          const x = 100 + (nodeSpacing * (index + 1));
          newNodes.push({
            id: `pole-${pole.id}`,
            type: pole.pole_type === 'olt' ? 'olt' : 'pole',
            pole,
            x,
            y: levelY,
            level,
          });
        });
      } else {
        // Left-Right layout (for wide monitors)
        const levelX = 100 + (level * levelSpacing);
        const totalHeight = Math.max(canvasHeight - 200, nodesInLevel * minNodeSpacing);
        const nodeSpacing = totalHeight / (nodesInLevel + 1);

        sortedPoles.forEach((pole, index) => {
          const y = 100 + (nodeSpacing * (index + 1));
          newNodes.push({
            id: `pole-${pole.id}`,
            type: pole.pole_type === 'olt' ? 'olt' : 'pole',
            pole,
            x: levelX,
            y,
            level,
          });
        });
      }
    });

    // Add virtual nodes (positioned between from and to nodes)
    virtualNodes.forEach((vNode) => {
      const fromNode = newNodes.find(n => n.id === `pole-${vNode.fromPoleId}`);
      const toNode = newNodes.find(n => n.id === `pole-${vNode.toPoleId}`);

      if (fromNode && toNode) {
        newNodes.push({
          id: vNode.id,
          type: vNode.type,
          label: vNode.label,
          x: (fromNode.x + toNode.x) / 2,
          y: (fromNode.y + toNode.y) / 2,
          level: (fromNode.level + toNode.level) / 2,
        });
      }
    });

    setNodes(newNodes);
    setLinks(finalLinks);
  }, [poles, cables, layoutDirection, canvasSize]);

  // Draw topology with zoom and pan
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save context state
    ctx.save();

    // Apply transformations
    ctx.translate(offsetX, offsetY);
    ctx.scale(scale, scale);

    // Draw links with improved styling
    links.forEach((link) => {
      const fromNode = nodes.find((n) => n.id === link.from);
      const toNode = nodes.find((n) => n.id === link.to);
      if (!fromNode || !toNode) return;

      // Calculate cable color based on length/loss
      let cableColor = '#3498db';
      let cableWidth = 3;

      if (link.cable) {
        const length = link.cable.length_meters;
        if (length > 1000) {
          cableColor = '#e74c3c'; // Red for long cables
          cableWidth = 4;
        } else if (length > 500) {
          cableColor = '#f39c12'; // Orange for medium cables
          cableWidth = 3;
        } else {
          cableColor = '#27ae60'; // Green for short cables
          cableWidth = 2;
        }
      }

      // Draw cable with gradient effect
      const gradient = ctx.createLinearGradient(fromNode.x, fromNode.y, toNode.x, toNode.y);
      gradient.addColorStop(0, cableColor);
      gradient.addColorStop(0.5, lightenColor(cableColor, 20));
      gradient.addColorStop(1, cableColor);

      ctx.beginPath();
      ctx.moveTo(fromNode.x, fromNode.y);
      ctx.lineTo(toNode.x, toNode.y);
      ctx.strokeStyle = gradient;
      ctx.lineWidth = cableWidth;
      ctx.stroke();

      // Draw cable label with background
      const midX = (fromNode.x + toNode.x) / 2;
      const midY = (fromNode.y + toNode.y) / 2;

      if (link.cable) {
        const labelText = `${link.cable.length_meters}m`;

        // Measure text for background
        ctx.font = '11px Arial';
        const textMetrics = ctx.measureText(labelText);
        const textWidth = textMetrics.width;
        const textHeight = 12;

        // Draw label background
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.fillRect(
          midX - textWidth / 2 - 3,
          midY - textHeight / 2 - 2,
          textWidth + 6,
          textHeight + 4
        );
        ctx.strokeStyle = cableColor;
        ctx.lineWidth = 1;
        ctx.strokeRect(
          midX - textWidth / 2 - 3,
          midY - textHeight / 2 - 2,
          textWidth + 6,
          textHeight + 4
        );

        // Draw label text
        ctx.fillStyle = '#2c3e50';
        ctx.textAlign = 'center';
        ctx.fillText(labelText, midX, midY + 2);
      }
    });

    // Draw nodes
    nodes.forEach((node) => {
      // Virtual nodes (passthrough/splice) - draw smaller
      if (node.type === 'passthrough' || node.type === 'splice') {
        ctx.beginPath();
        ctx.arc(node.x, node.y, 8, 0, 2 * Math.PI);

        if (node.type === 'passthrough') {
          ctx.fillStyle = '#9b59b6'; // Purple for passthrough
        } else {
          ctx.fillStyle = '#e67e22'; // Orange for splice
        }

        ctx.fill();
        ctx.strokeStyle = '#2c3e50';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Draw label
        ctx.fillStyle = '#2c3e50';
        ctx.font = '9px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(node.label || '', node.x, node.y + 20);

        return;
      }

      // Regular nodes (poles/OLT) with improved styling
      const nodeRadius = 25;
      const pole = node.pole;

      // Draw node with type-specific styling
      ctx.beginPath();

      if (pole?.pole_type === 'olt') {
        // OLT - Square shape
        ctx.rect(node.x - nodeRadius, node.y - nodeRadius, nodeRadius * 2, nodeRadius * 2);
        ctx.fillStyle = '#e74c3c'; // Red
        ctx.fill();
        ctx.strokeStyle = '#c0392b';
        ctx.lineWidth = 3;
        ctx.stroke();
      } else if (pole?.pole_type === 'splitter_point') {
        // Splitter - Diamond shape
        ctx.moveTo(node.x, node.y - nodeRadius);
        ctx.lineTo(node.x + nodeRadius, node.y);
        ctx.lineTo(node.x, node.y + nodeRadius);
        ctx.lineTo(node.x - nodeRadius, node.y);
        ctx.closePath();
        ctx.fillStyle = '#f39c12'; // Orange
        ctx.fill();
        ctx.strokeStyle = '#e67e22';
        ctx.lineWidth = 3;
        ctx.stroke();
      } else if (pole?.pole_type === 'building') {
        // Building - House shape
        ctx.rect(node.x - nodeRadius * 0.8, node.y - nodeRadius * 0.5, nodeRadius * 1.6, nodeRadius * 1.2);
        ctx.fillStyle = '#27ae60'; // Green
        ctx.fill();
        // Roof
        ctx.beginPath();
        ctx.moveTo(node.x - nodeRadius * 0.8, node.y - nodeRadius * 0.5);
        ctx.lineTo(node.x, node.y - nodeRadius);
        ctx.lineTo(node.x + nodeRadius * 0.8, node.y - nodeRadius * 0.5);
        ctx.closePath();
        ctx.fillStyle = '#2c3e50';
        ctx.fill();
        ctx.strokeStyle = '#1e8449';
        ctx.lineWidth = 2;
        ctx.stroke();
      } else {
        // Regular pole - Circle
        ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI);
        ctx.fillStyle = '#3498db'; // Blue
        ctx.fill();
        ctx.strokeStyle = '#2980b9';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Draw node label with background
      const labelText = node.pole?.name || '';
      const labelY = node.y + 45;

      // Measure text for background
      ctx.font = 'bold 12px Arial';
      const textMetrics = ctx.measureText(labelText);
      const textWidth = textMetrics.width;
      const textHeight = 16;

      // Draw label background
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(
        node.x - textWidth / 2 - 4,
        labelY - textHeight / 2 - 2,
        textWidth + 8,
        textHeight + 4
      );
      ctx.strokeStyle = '#bdc3c7';
      ctx.lineWidth = 1;
      ctx.strokeRect(
        node.x - textWidth / 2 - 4,
        labelY - textHeight / 2 - 2,
        textWidth + 8,
        textHeight + 4
      );

      // Draw label text
      ctx.fillStyle = '#2c3e50';
      ctx.textAlign = 'center';
      ctx.fillText(labelText, node.x, labelY + 2);

      // Draw type indicator
      let typeIcon = '';
      let typeColor = '#7f8c8d';
      if (pole?.pole_type === 'olt') {
        typeIcon = 'OLT';
        typeColor = '#e74c3c';
      } else if (pole?.pole_type === 'splitter_point') {
        typeIcon = `1:${pole.splitter_type?.split(':')[1] || '8'}`;
        typeColor = '#f39c12';
      } else if (pole?.pole_type === 'building') {
        typeIcon = `${pole.expected_customers || 1}👥`;
        typeColor = '#27ae60';
      }

      if (typeIcon) {
        ctx.fillStyle = typeColor;
        ctx.font = 'bold 10px Arial';
        ctx.fillText(typeIcon, node.x, node.y - 35);
      }

      // Draw cassette indicator
      if (node.pole?.cassette?.has_cassette) {
        ctx.fillStyle = '#f39c12';
        ctx.font = '12px Arial';
        ctx.fillText('📦', node.x + 20, node.y - 20);
      }
    });

    // Restore context state
    ctx.restore();
  }, [nodes, links, scale, offsetX, offsetY]);

  // Zoom functions
  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev / 1.2, 0.1));
  };

  const handleResetView = () => {
    setScale(1);
    setOffsetX(0);
    setOffsetY(0);
  };

  // Mouse wheel zoom
  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Zoom towards mouse position
    const zoom = e.deltaY < 0 ? 1.1 : 0.9;
    const newScale = Math.min(Math.max(scale * zoom, 0.1), 5);

    // Adjust offset to zoom towards mouse
    const scaleChange = newScale / scale;
    setOffsetX(mouseX - (mouseX - offsetX) * scaleChange);
    setOffsetY(mouseY - (mouseY - offsetY) * scaleChange);
    setScale(newScale);
  };

  // Pan functions
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (e.button === 0) { // Left click
      setIsPanning(true);
      setStartPan({ x: e.clientX - offsetX, y: e.clientY - offsetY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isPanning) {
      setOffsetX(e.clientX - startPan.x);
      setOffsetY(e.clientY - startPan.y);
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Helper function to get pole name by ID
  const getPoleNameById = (poleId: number): string => {
    const pole = poles.find((p: Pole) => p.id === poleId);
    return pole?.name || `Pole #${poleId}`;
  };

  // Helper function to get cable description
  const getCableDescription = (cableId: number | undefined): string => {
    if (!cableId) return 'Unknown Cable';
    const cable = cables.find((c: Cable) => c.id === cableId);
    if (!cable) return `Cable #${cableId}`;

    const fromPole = getPoleNameById(cable.from_pole_id);
    const toPole = getPoleNameById(cable.to_pole_id);
    return `${fromPole} → ${toPole}`;
  };

  // Splittre, ktoré sa k vybranému stĺpu vzťahujú — vrátane tých v jeho
  // zákazníckej kazete.
  //
  // Zákaznícky splitter totiž takmer vždy sedí v zákazníckej kazete, a tá je
  // v appke samostatný VIRTUÁLNY stĺp. Topológia virtuálne stĺpy zámerne
  // nekreslí (inak by bola plná dvojčiat), takže sa k nim nedalo ani
  // prekliknúť — a zákazníkov nebolo v topológii vidieť vôbec. V produkcii
  // takto visí 121 zo 138 zákazníkov, čiže drvivá väčšina.
  const splittreStlpa = useMemo<SplitterZKazety[]>(() => {
    if (!selectedPole) return [];
    const zoznam: SplitterZKazety[] = [];
    ((selectedPole.cassette?.splitters || []) as any[]).forEach((sp) =>
      zoznam.push({ splitter: sp, zoZakaznickej: false }));
    const dvojca = poles.find(
      (p) => (p as any).is_virtual && (p as any).parent_pole_id === selectedPole.id);
    (((dvojca?.cassette as any)?.splitters || []) as any[]).forEach((sp) =>
      zoznam.push({ splitter: sp, zoZakaznickej: true }));
    return zoznam;
  }, [selectedPole, poles]);

  // Get connected cables for selected pole
  const getConnectedCables = () => {
    if (!selectedPole) return { incoming: [], outgoing: [] };

    // Find all cables connected to this pole (in either direction)
    const connectedCables = cables.filter((c: Cable) =>
      c.to_pole_id === selectedPole.id || c.from_pole_id === selectedPole.id
    );

    // Separate into incoming and outgoing based on direction
    const incoming = connectedCables.filter((c: Cable) => c.to_pole_id === selectedPole.id);
    const outgoing = connectedCables.filter((c: Cable) => c.from_pole_id === selectedPole.id);

    return { incoming, outgoing };
  };

  // Handle canvas click (for selecting poles)
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isPanning) return; // Don't select if we were panning

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Transform mouse coordinates to canvas coordinates
    const canvasX = (mouseX - offsetX) / scale;
    const canvasY = (mouseY - offsetY) / scale;

    // Find clicked node
    const clickedNode = nodes.find((node) => {
      const dx = canvasX - node.x;
      const dy = canvasY - node.y;
      const radius = (node.type === 'passthrough' || node.type === 'splice') ? 8 : 20;
      return Math.sqrt(dx * dx + dy * dy) <= radius;
    });

    if (clickedNode?.pole) {
      setSelectedPole(clickedNode.pole);
    }
  };
  return (
    <div className="topology-view">
      <div className="topology-header">
        <h2>🌐 Topológia siete</h2>
        <div className="topology-header-controls">
          <div className="topology-legend">
            <div className="legend-item">
              <span className="legend-color olt"></span>
              <span>OLT</span>
            </div>
            <div className="legend-item">
              <span className="legend-color pole"></span>
              <span>Stĺpik</span>
            </div>
            <div className="legend-item">
              <span>📦</span>
              <span>Má kazetu</span>
            </div>
          </div>
          <button
            onClick={() => setLayoutDirection(prev => prev === 'TB' ? 'LR' : 'TB')}
            style={{
              padding: '8px 16px',
              background: layoutDirection === 'LR' ? '#667eea' : 'white',
              color: layoutDirection === 'LR' ? 'white' : '#667eea',
              border: '2px solid #667eea',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s',
            }}
            title="Prepnúť orientáciu grafu"
          >
            {layoutDirection === 'TB' ? '⬇️ Zhora-Dole' : '➡️ Zľava-Doprava'}
          </button>
        </div>
      </div>

      <div className="topology-content">
        <div ref={containerRef} className="topology-canvas-container" style={{ position: 'relative' }}>
          {/* Zoom Controls */}
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            zIndex: 10,
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <button
              onClick={handleZoomIn}
              style={{
                background: 'white',
                border: 'none',
                borderBottom: '1px solid #e0e0e0',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '12px 16px',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              title="Priblížiť"
            >
              +
            </button>
            <button
              onClick={handleZoomOut}
              style={{
                background: 'white',
                border: 'none',
                borderBottom: '1px solid #e0e0e0',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '12px 16px',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              title="Oddialiť"
            >
              −
            </button>
            <button
              onClick={handleResetView}
              style={{
                background: 'white',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '12px 16px',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              title="Obnoviť zobrazenie"
            >
              ⊡
            </button>
          </div>

          {/* Zoom Level Indicator */}
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 10,
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            padding: '8px 12px',
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#666',
          }}>
            {Math.round(scale * 100)}%
          </div>

          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            onClick={handleCanvasClick}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
          />
        </div>

        {selectedPole && (
          <div className="topology-sidebar">
            <div className="sidebar-header">
              <h3>{selectedPole.name}</h3>
              <button onClick={() => setSelectedPole(null)}>✕</button>
            </div>

            <div className="sidebar-content">
              <div className="info-section">
                <h4>📍 Základné údaje</h4>
                <p><strong>Typ:</strong> {poleTypeIcon(selectedPole.pole_type)} {poleTypeLabel(selectedPole.pole_type)}</p>
                <p><strong>Poloha:</strong> {Number(selectedPole.latitude).toFixed(6)}, {Number(selectedPole.longitude).toFixed(6)}</p>
                {selectedPole.pole_type === 'olt' && selectedPole.olt_port_count && (
                  <p><strong>Porty:</strong> {selectedPole.olt_port_count}</p>
                )}
              </div>

              {/* Connected Cables */}
              {(() => {
                const { incoming, outgoing } = getConnectedCables();
                return (incoming.length > 0 || outgoing.length > 0) && (
                  <div className="info-section">
                    <h4>🔌 Pripojené káble</h4>

                    {incoming.length > 0 && (
                      <div style={{ marginBottom: '12px' }}>
                        <p style={{ fontWeight: 'bold', color: '#2196f3', marginBottom: '4px' }}>
                          ⬇️ Prichádzajúce ({incoming.length})
                        </p>
                        {incoming.map((cable: Cable) => (
                          <div key={cable.id} style={{
                            marginLeft: '8px',
                            marginBottom: '6px',
                            padding: '6px',
                            background: '#f0f8ff',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <p style={{ margin: '2px 0' }}>
                              <strong>{getPoleNameById(cable.from_pole_id)}</strong> → {selectedPole.name}
                            </p>
                            <p style={{ margin: '2px 0', color: '#666' }}>
                              📏 {cable.length_meters}m | 🧵 {cable.cable_type || '12-fiber'}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {outgoing.length > 0 && (
                      <div>
                        <p style={{ fontWeight: 'bold', color: '#4caf50', marginBottom: '4px' }}>
                          ⬆️ Odchádzajúce ({outgoing.length})
                        </p>
                        {outgoing.map((cable: Cable) => (
                          <div key={cable.id} style={{
                            marginLeft: '8px',
                            marginBottom: '6px',
                            padding: '6px',
                            background: '#f0fff0',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <p style={{ margin: '2px 0' }}>
                              {selectedPole.name} → <strong>{getPoleNameById(cable.to_pole_id)}</strong>
                            </p>
                            <p style={{ margin: '2px 0', color: '#666' }}>
                              📏 {cable.length_meters}m | 🧵 {cable.cable_type || '12-fiber'}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {(selectedPole.cassette?.has_cassette || splittreStlpa.length > 0) && (
                <div className="info-section">
                  <h4>📦 Nastavenie kazety</h4>
                  
                  {/* Pass-Through */}
                  {selectedPole.cassette?.pass_through_cables && selectedPole.cassette?.pass_through_cables.length > 0 && (
                    <div className="cassette-section">
                      <h5>🔄 Priechodné káble ({selectedPole.cassette?.pass_through_cables.length})</h5>
                      {selectedPole.cassette?.pass_through_cables.map((pt, idx) => (
                        <div key={idx} className="cassette-item" style={{
                          background: '#fff9e6',
                          padding: '8px',
                          borderRadius: '4px',
                          marginBottom: '8px',
                          fontSize: '12px'
                        }}>
                          <p style={{ margin: '4px 0', fontWeight: 'bold' }}>
                            📥 Z: {getCableDescription(pt.from_cable_id)}
                          </p>
                          <p style={{ margin: '4px 0', fontWeight: 'bold' }}>
                            📤 DO: {getCableDescription(pt.to_cable_id)}
                          </p>
                          <p style={{ margin: '4px 0', color: '#666' }}>
                            🧵 Odpichnuté vlákna: {pt.fibers.filter(f => f.is_tapped).length}/12
                          </p>
                          {pt.fibers.filter(f => f.is_tapped).length > 0 && (
                            <p style={{ margin: '4px 0', fontSize: '11px', color: '#888' }}>
                              Odpichnuté: {pt.fibers.filter(f => f.is_tapped).map(f => `#${f.fiber_number}`).join(', ')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Splices */}
                  {selectedPole.cassette?.splices && selectedPole.cassette?.splices.length > 0 && (
                    <div className="cassette-section">
                      <h5>🔗 Zvary ({selectedPole.cassette?.splices.length})</h5>
                      {selectedPole.cassette?.splices.map((splice, idx) => (
                        <div key={idx} className="cassette-item" style={{
                          background: '#f0f0ff',
                          padding: '8px',
                          borderRadius: '4px',
                          marginBottom: '8px',
                          fontSize: '12px'
                        }}>
                          <p style={{ margin: '4px 0' }}>
                            <strong>Z:</strong> {getCableDescription(splice.from_cable_id)}
                          </p>
                          <p style={{ margin: '4px 0', marginLeft: '16px', color: '#666' }}>
                            🧵 Vlákno č.{splice.from_fiber}
                          </p>
                          <p style={{ margin: '4px 0' }}>
                            <strong>Do:</strong> {getCableDescription(splice.to_cable_id)}
                          </p>
                          <p style={{ margin: '4px 0', marginLeft: '16px', color: '#666' }}>
                            🧵 Vlákno č.{splice.to_fiber}
                          </p>
                          <p style={{ margin: '4px 0', color: '#e74c3c', fontWeight: 'bold' }}>
                            📉 Útlm: {splice.loss_db || 0.1} dB
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Splitters — vrátane tých v zákazníckej kazete, viď splittreStlpa */}
                  {splittreStlpa.length > 0 && (
                    <div className="cassette-section">
                      <h5>📡 Splittery ({splittreStlpa.length})</h5>
                      {splittreStlpa.map(({ splitter, zoZakaznickej }: SplitterZKazety, idx: number) => (
                        <div key={idx} className="cassette-item" style={{
                          background: '#fff0f5',
                          padding: '8px',
                          borderRadius: '4px',
                          marginBottom: '8px',
                          fontSize: '12px'
                        }}>
                          <p style={{ margin: '4px 0', fontWeight: 'bold', color: '#ff9800' }}>
                            🔀 Typ: {splitter.type}
                            {zoZakaznickej && (
                              <span style={{
                                marginLeft: '6px',
                                padding: '1px 6px',
                                borderRadius: '999px',
                                background: '#e8f5e9',
                                color: '#2e7d32',
                                fontSize: '10px',
                                fontWeight: 600,
                              }}>
                                zákaznícka kazeta
                              </span>
                            )}
                          </p>
                          <p style={{ margin: '4px 0' }}>
                            <strong>📥 Vstup:</strong>
                          </p>
                          <p style={{ margin: '4px 0', marginLeft: '16px', color: '#666' }}>
                            {getCableDescription(splitter.input_cable_id)}
                          </p>
                          <p style={{ margin: '4px 0', marginLeft: '16px', color: '#666' }}>
                            🧵 Vlákno č.{splitter.input_fiber}
                          </p>
                          {(() => {
                            // Zákazník je zapísaný priamo na výstupe splittra
                            // (customer_name, pribudne pri pripojení v montážnom
                            // dialógu). Panel ho doteraz nezobrazoval vôbec,
                            // takže z topológie nebolo vidieť, kto na splittri
                            // visí a koľko portov je ešte voľných.
                            const obsadene = splitter.outputs.filter(
                              (o: any) => o && String(o.customer_name || '').trim(),
                            ).length;
                            return (
                              <p style={{ margin: '4px 0', fontWeight: 'bold' }}>
                                📤 Výstupy: {splitter.outputs.length}
                                {' — '}
                                <span style={{ color: '#2e7d32' }}>obsadené {obsadene}</span>
                                {', '}
                                <span style={{ color: '#b26a00' }}>
                                  voľné {splitter.outputs.length - obsadene}
                                </span>
                              </p>
                            );
                          })()}
                          {(splitter.outputs as any[]).map((output: any, outIdx: number) => {
                            const zakaznik = String((output as any).customer_name || '').trim();
                            const adresa = String((output as any).customer_address || '').trim();
                            return (
                              <div key={outIdx} style={{
                                marginLeft: '16px',
                                marginTop: '4px',
                                padding: '4px',
                                background: 'white',
                                borderRadius: '3px',
                                fontSize: '11px',
                                borderLeft: `3px solid ${zakaznik ? '#2e7d32' : '#d7d7d7'}`,
                              }}>
                                <p style={{ margin: '2px 0' }}>
                                  #{outIdx + 1}: {getCableDescription(output.cable_id)}
                                </p>
                                <p style={{ margin: '2px 0', color: '#888' }}>
                                  🧵 Vlákno č.{output.fiber || 'N/A'}
                                </p>
                                {zakaznik ? (
                                  <>
                                    <p style={{ margin: '2px 0', fontWeight: 600, color: '#2e7d32' }}>
                                      👤 {zakaznik}
                                    </p>
                                    {adresa && (
                                      <p style={{ margin: '2px 0', color: '#777' }}>
                                        🏠 {adresa}
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <p style={{ margin: '2px 0', color: '#b26a00' }}>
                                    ⬜ voľný port
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

